import { useCallback, useEffect, useState } from 'react';
import {
  fetchAccounts, createAccount, updateAccount,
  fetchIntel, addIntel, applyIntelToAtlas,
  fetchPortalUsers, invitePortalUser, setPortalUserStatus,
  fetchQuoteRequests, setQuoteRequest,
  searchCustomers, placeSuggestions, createAccountFromSite,
  fetchContacts, addContact, removeContact, fetchAudit, setDistributor, fetchDistributor,
  INTEL_METRICS, INTEL_SOURCES,
  type Account, type Intel, type PortalUser, type QuoteRequest,
  type Contact, type AuditRow, type SearchHit,
} from '../lib/customers';
import { countries } from '../lib/countries';
import { fetchProducts, type Product } from '../lib/trade';
import { fetchCases, CASE_KINDS, CASE_STATUSES, type SupportCase } from '../lib/support';
import { onFail } from '../lib/errors';
import './customers.css';

/* =====================================================================================
   Customers: the account database and the portal behind it.

   The provenance deal, in one sentence: what a person types here is stamped 'operator'
   and Atlas may never overwrite it, while the intel recorded here is what corrects
   Atlas, attributed and auditable. Portal access is invitation-only, and the quote
   requests those invitees raise land at the top of this screen until answered.
   ===================================================================================== */

const fmtD = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString('en-GB',
  { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const STATUS_PILL: Record<string, string> = {
  prospect: 'mute', engaged: 'info', customer: 'ok', dormant: 'mute', lost: 'warn', excluded: 'warn',
  invited: 'info', active: 'ok', disabled: 'mute',
  new: 'warn', in_progress: 'info', quoted: 'ok', declined: 'mute',
};
const pill = (s: string) => <span className={`pill ${STATUS_PILL[s] ?? 'mute'}`}>{s.replace(/_/g, ' ')}</span>;

export function CustomersView() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [requests, setRequests] = useState<QuoteRequest[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchAccounts().then(setAccounts).catch(onFail('The customer database', () => setAccounts([])));
    fetchQuoteRequests().then(setRequests).catch(() => setRequests([]));
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (q.trim().length < 2) { setHits(null); return; }
    const t = setTimeout(() => {
      searchCustomers(q.trim()).then(setHits).catch(() => setHits(null));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const open = accounts.find((a) => a.id === openId) ?? null;
  const filtered = accounts.filter((a) => !q
    || a.name.toLowerCase().includes(q.toLowerCase())
    || (a.city ?? '').toLowerCase().includes(q.toLowerCase())
    || a.country.toLowerCase().includes(q.toLowerCase()));
  const newRequests = requests.filter((r) => r.status === 'new' || r.status === 'in_progress');

  return (
    <div className="page customers">
      <div className="page-head">
        <h1 className="page-title">Customers</h1>
        <p className="page-sub">
          Accounts, the intel they teach us, and who holds a portal key. What you type here
          is never overwritten by Atlas; what you learn here is what corrects Atlas.
        </p>
      </div>

      {msg && <p className="block-note" role="status">{msg}</p>}

      {newRequests.length > 0 && (
        <section className="card cu-requests">
          <h2>Quote requests from the portal</h2>
          {newRequests.map((r) => (
            <RequestRow key={r.id} r={r} products={products}
              accountName={accounts.find((a) => a.id === r.account_id)?.name ?? ''}
              onChanged={load} onMsg={setMsg} />
          ))}
        </section>
      )}

      <div className="toolbar cu-searchwrap">
        <input className="input cu-search"
               placeholder="Search centres, people (name or email), cities, countries…"
               value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn primary" onClick={() => setAddingAccount(true)}>New account</button>
        {hits && (
          <div className="cu-hits">
            {hits.length === 0 && <div className="cu-hit muted">Nothing matches anywhere.</div>}
            {hits.map((h) => (
              <div key={h.kind + h.id} className="cu-hit" onClick={async () => {
                try {
                  if (h.account_id) { setOpenId(h.account_id); setQ(''); return; }
                  if (h.kind === 'site') {
                    const id = await createAccountFromSite(h.id);
                    setMsg(`${h.label} is now a customer account, prefilled from Atlas.`);
                    load(); setOpenId(id); setQ('');
                  } else {
                    setMsg('That person has no customer account attached yet.');
                  }
                } catch (e) { setMsg(String((e as Error).message)); }
              }}>
                <span className={`pill ${h.kind === 'account' ? 'ok' : h.kind === 'person' ? 'info' : 'mute'}`}>
                  {h.kind === 'site' ? 'Atlas' : h.kind}
                </span>
                <span className="cu-hit-label">{h.label}</span>
                <span className="cell-sub">{h.sublabel}
                  {h.kind === 'site' && !h.account_id && <strong> · click to make customer</strong>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <section className="cu-grid">
        {filtered.length === 0 && <p className="module-empty">No accounts match.</p>}
        {filtered.map((a) => (
          <article key={a.id} className="card cu-card" onClick={() => setOpenId(a.id)}>
            <div className="cu-card-head">
              <strong>{a.name}</strong>
              {pill(a.status)}
            </div>
            <div className="cell-sub">
              {[a.city, a.country].filter(Boolean).join(', ')}
              {a.tier && <> · {a.tier.replace('_', ' ')}</>}
              {a.atlas_site_id && <> · linked to Atlas</>}
              {a.credit_hold && <span className="pill warn">credit hold</span>}
            </div>
          </article>
        ))}
      </section>

      {addingAccount && (
        <AddAccount onClose={() => setAddingAccount(false)}
          onDone={() => { setAddingAccount(false); setMsg('Account created.'); load(); }} />
      )}
      {open && (
        <AccountDrawer a={open} onClose={() => setOpenId(null)}
          onChanged={() => load()} onMsg={setMsg} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ portal requests */
function RequestRow({ r, products, accountName, onChanged, onMsg }: {
  r: QuoteRequest; products: Product[]; accountName: string;
  onChanged: () => void; onMsg: (s: string) => void;
}) {
  const named = (id?: string) => products.find((p) => p.id === id)?.name ?? id ?? '';
  return (
    <div className="cu-req">
      <div>
        <strong>{accountName}</strong> {pill(r.status)}
        <div className="cell-sub">{fmtD(r.created_at)}</div>
        {(r.lines ?? []).length > 0 && (
          <ul className="cu-req-lines">
            {r.lines.map((l, i) => (
              <li key={i}>{l.quantity ?? '?'} × {named(l.product_id)}{l.note && <> · {l.note}</>}</li>
            ))}
          </ul>
        )}
        {r.message && <p className="cu-req-msg">“{r.message}”</p>}
      </div>
      <div className="cu-req-actions">
        {r.status === 'new' && (
          <button className="btn" onClick={() =>
            setQuoteRequest(r.id, { status: 'in_progress' }).then(onChanged)
              .catch((e) => onMsg(e.message))}>Take it</button>
        )}
        <button className="btn" onClick={() =>
          setQuoteRequest(r.id, { status: 'quoted' })
            .then(() => { onMsg('Marked quoted. Build the quote in Trade and send it.'); onChanged(); })
            .catch((e) => onMsg(e.message))}>Mark quoted</button>
        <button className="link-btn" onClick={() =>
          setQuoteRequest(r.id, { status: 'declined' }).then(onChanged)
            .catch((e) => onMsg(e.message))}>Decline</button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ add account */
function AddAccount({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ name: '', country: 'GBR', city: '', isDistributor: false });
  const [places, setPlaces] = useState<{ city: string; country: string }[]>([]);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { placeSuggestions().then(setPlaces).catch(() => setPlaces([])); }, []);
  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer cu-drawer">
        <h2>New account</h2>
        {err && <p className="block-note">{err}</p>}
        <label className="cf-field"><span>Name</span>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></label>
        <div className="cu-two">
          <label className="cf-field"><span>Country</span>
            <input className="input" list="cu-countries" value={f.country}
                   onChange={(e) => setF({ ...f, country: e.target.value })} /></label>
          <label className="cf-field"><span>City</span>
            <input className="input" list="cu-cities" value={f.city}
                   onChange={(e) => setF({ ...f, city: e.target.value })} /></label>
        </div>
        <datalist id="cu-countries">
          {countries().map((c) => <option key={c.iso3} value={c.iso3}>{c.name}</option>)}
        </datalist>
        <datalist id="cu-cities">
          {places.filter((pl) => !f.country || pl.country === f.country)
            .map((pl, i) => <option key={i} value={pl.city} />)}
        </datalist>
        <label className="lv-check"><input type="checkbox" checked={f.isDistributor}
          onChange={(e) => setF({ ...f, isDistributor: e.target.checked })} />
          <span>This account is a distributor (it can be set as other customers' commercial parent)</span></label>
        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            try {
              if (!f.name.trim()) throw new Error('A name, at least.');
              const { iso3For } = await import('../lib/countries');
              const iso = iso3For(f.country);
              if (!iso) throw new Error('Pick a country from the list (or type its 3-letter code).');
              await createAccount({ name: f.name.trim(), country: iso, city: f.city || undefined,
                kinds: f.isDistributor ? ['distributor'] : undefined });
              onDone();
            } catch (e) { setErr(String((e as Error).message)); }
          }}>Create</button>
          <button className="btn" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ account drawer */
function AccountDrawer({ a, onClose, onChanged, onMsg }: {
  a: Account; onClose: () => void; onChanged: () => void; onMsg: (s: string) => void;
}) {
  const [intel, setIntel] = useState<Intel[]>([]);
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [cases, setCases] = useState<SupportCase[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [distributor, setDistributorState] = useState<string | ''>('');
  const [places, setPlaces] = useState<{ city: string; country: string }[]>([]);
  const [allAccounts, setAllAccounts] = useState<Account[]>([]);
  const [f, setF] = useState({ website: a.website ?? '', main_phone: a.main_phone ?? '',
    invoice_email: a.invoice_email ?? '', status: a.status, tier: a.tier ?? '',
    payment_terms_days: a.payment_terms_days ?? '' as number | '',
    vat_number: a.vat_number ?? '', legal_name: a.legal_name ?? '',
    street: a.address?.street ?? '', postal_code: a.address?.postal_code ?? '',
    addr_city: a.address?.city ?? a.city ?? '', addr_country: a.address?.country ?? a.country ?? '' });
  const [err, setErr] = useState<string | null>(null);
  const [addingIntel, setAddingIntel] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [inviting, setInviting] = useState(false);

  const reload = useCallback(() => {
    fetchIntel(a.id).then(setIntel).catch(() => setIntel([]));
    fetchPortalUsers(a.id).then(setUsers).catch(() => setUsers([]));
    fetchCases(a.id).then(setCases).catch(() => setCases([]));
    fetchContacts(a.id).then(setContacts).catch(() => setContacts([]));
    fetchAudit(a.id).then(setAudit).catch(() => setAudit([]));
    fetchDistributor(a.id).then((d) => setDistributorState(d ?? '')).catch(() => null);
    placeSuggestions().then(setPlaces).catch(() => setPlaces([]));
    fetchAccounts().then(setAllAccounts).catch(() => setAllAccounts([]));
  }, [a.id]);
  useEffect(() => { reload(); }, [reload]);

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div className="drawer cu-drawer">
        <h2>{a.name} {pill(a.status)}</h2>
        {err && <p className="block-note">{err}</p>}
        <p className="muted">
          Fields you save here are stamped as yours and survive every Atlas refresh.
          {a.atlas_site_id
            ? ' Linked to its Atlas site: atlas-owned fields follow the market model automatically, and each change lands in the history below.'
            : ' Not yet linked to an Atlas site (search for the centre on the Customers screen and click it to link).'}
        </p>

        <div className="cu-two">
          <label className="cf-field"><span>Status</span>
            <select className="input sel" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
              {['prospect', 'engaged', 'customer', 'dormant', 'lost', 'excluded'].map((s) =>
                <option key={s} value={s}>{s}</option>)}
            </select></label>
          <label className="cf-field"><span>Tier</span>
            <select className="input sel" value={f.tier} onChange={(e) => setF({ ...f, tier: e.target.value })}>
              <option value="">unset</option>
              {['tier_1', 'tier_2', 'tier_3'].map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select></label>
        </div>
        <div className="cu-two">
          <label className="cf-field"><span>Website</span>
            <input className="input" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} /></label>
          <label className="cf-field"><span>Phone</span>
            <input className="input" value={f.main_phone} onChange={(e) => setF({ ...f, main_phone: e.target.value })} /></label>
        </div>
        <div className="cu-two">
          <label className="cf-field"><span>Legal name (invoices and quotes)</span>
            <input className="input" value={f.legal_name} onChange={(e) => setF({ ...f, legal_name: e.target.value })} /></label>
          <label className="cf-field"><span>VAT number</span>
            <input className="input" value={f.vat_number} onChange={(e) => setF({ ...f, vat_number: e.target.value })} /></label>
        </div>
        <h3 className="cu-h3">Address (appears on quotes)</h3>
        <label className="cf-field"><span>Street and number</span>
          <input className="input" value={f.street} onChange={(e) => setF({ ...f, street: e.target.value })} /></label>
        <div className="cu-three">
          <label className="cf-field"><span>Postal code</span>
            <input className="input" value={f.postal_code} onChange={(e) => setF({ ...f, postal_code: e.target.value })} /></label>
          <label className="cf-field"><span>City</span>
            <input className="input" list="cud-cities" value={f.addr_city}
                   onChange={(e) => setF({ ...f, addr_city: e.target.value })} /></label>
          <label className="cf-field"><span>Country</span>
            <input className="input" list="cud-countries" value={f.addr_country}
                   onChange={(e) => setF({ ...f, addr_country: e.target.value })} /></label>
        </div>
        <datalist id="cud-countries">
          {countries().map((c) => <option key={c.iso3} value={c.iso3}>{c.name}</option>)}
        </datalist>
        <datalist id="cud-cities">
          {places.map((pl, i) => <option key={i} value={pl.city} />)}
        </datalist>
        <label className="cf-field"><span>Distributor (commercial parent, if sold through one)</span>
          <select className="input sel" value={distributor}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDistributor(a.id, v || null)
                      .then(() => { setDistributorState(v); onMsg(v ? 'Distributor set.' : 'Distributor cleared.'); reload(); })
                      .catch((x) => setErr(x.message));
                  }}>
            <option value="">None: direct customer</option>
            {allAccounts.filter((x) => x.kinds?.includes('distributor') && x.id !== a.id)
              .map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select></label>
        <div className="cu-two">
          <label className="cf-field"><span>Invoice email</span>
            <input className="input" value={f.invoice_email} onChange={(e) => setF({ ...f, invoice_email: e.target.value })} /></label>
          <label className="cf-field"><span>Payment terms (days)</span>
            <input className="input" type="number" value={f.payment_terms_days}
                   onChange={(e) => setF({ ...f, payment_terms_days: e.target.value === '' ? '' : Number(e.target.value) })} /></label>
        </div>
        <div className="exp-decide-actions">
          <button className="btn primary" onClick={async () => {
            setErr(null);
            try {
              await updateAccount(a.id, {
                website: f.website || null, main_phone: f.main_phone || null,
                invoice_email: f.invoice_email || null, status: f.status,
                tier: f.tier || null, legal_name: f.legal_name || null,
                vat_number: f.vat_number || null,
                address: { ...(a.address ?? {}), street: f.street, postal_code: f.postal_code,
                  city: f.addr_city, country: f.addr_country },
                payment_terms_days: f.payment_terms_days === '' ? null : f.payment_terms_days,
              } as Partial<Account>, a);
              onMsg('Saved, and stamped as operator truth.'); onChanged();
            } catch (e) { setErr(String((e as Error).message)); }
          }}>Save</button>
          <button className="btn" onClick={onClose}>Done</button>
        </div>

        <section className="cu-section">
          <div className="cu-sec-head">
            <h3>Commercial intel</h3>
            <button className="btn" onClick={() => setAddingIntel(true)}>Record intel</button>
          </div>
          {intel.length === 0 && <p className="muted">Nothing recorded yet. Orders and conversations belong here.</p>}
          {intel.map((i) => (
            <div key={i.id} className="cu-row">
              <div>
                <strong>{INTEL_METRICS.find(([k]) => k === i.metric)?.[1] ?? i.metric}</strong>
                <div className="cell-sub">
                  {JSON.stringify(i.value)} · {i.source} · {fmtD(i.observed_on)}
                  {i.note && <> · {i.note}</>}
                  {i.applied_to_atlas_at && <span className="pill ok">in Atlas</span>}
                </div>
              </div>
              {i.metric === 'transplant_volume' && !i.applied_to_atlas_at && (
                <button className="btn" onClick={() =>
                  applyIntelToAtlas(i.id)
                    .then(() => { onMsg('Written into Atlas as a verified, attributed row.'); reload(); })
                    .catch((e) => onMsg(e.message))}>Apply to Atlas</button>
              )}
            </div>
          ))}
        </section>

        <section className="cu-section">
          <div className="cu-sec-head">
            <h3>Contacts</h3>
            <button className="btn" onClick={() => setAddingContact(true)}>Add a contact</button>
          </div>
          {contacts.length === 0 && <p className="muted">Nobody recorded yet. Lab directors, surgeons, procurement: they all belong here, searchable by name or email.</p>}
          {contacts.map((c) => (
            <div key={c.affiliation_id} className="cu-row">
              <div>
                <strong>{c.full_name}</strong>
                {c.role_title && <span className="pill mute">{c.role_title}</span>}
                <div className="cell-sub">
                  {[c.work_email, c.work_phone].filter(Boolean).join(' · ')}
                  {c.notes && <> · {c.notes}</>}
                </div>
              </div>
              <button className="link-btn" onClick={() =>
                removeContact(c.affiliation_id).then(() => { onMsg('Contact removed.'); reload(); })
                  .catch((x) => setErr(x.message))}>remove</button>
            </div>
          ))}
          {addingContact && (
            <AddContactForm accountId={a.id}
              onClose={() => setAddingContact(false)}
              onDone={() => { setAddingContact(false); onMsg('Contact added.'); reload(); }} />
          )}
        </section>

        <section className="cu-section">
          <div className="cu-sec-head">
            <h3>Support cases</h3>
          </div>
          {cases.length === 0 && <p className="muted">No cases for this account. Long may it last.</p>}
          {cases.map((sc) => (
            <div key={sc.id} className="cu-row">
              <div>
                <strong>{sc.number}</strong> · {sc.title}
                <div className="cell-sub">
                  {(CASE_KINDS.find(([k]) => k === sc.kind)?.[1]) ?? sc.kind}
                  {' '}· {(CASE_STATUSES.find(([k]) => k === sc.status)?.[1]) ?? sc.status}
                  {' '}· opened {fmtD(sc.opened_at)} · worked in Support
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className="cu-section">
          <div className="cu-sec-head">
            <h3>Portal access</h3>
            <button className="btn" onClick={() => setInviting(true)}>Invite a contact</button>
          </div>
          {users.length === 0 && <p className="muted">Nobody at this account holds a portal key yet.</p>}
          {users.map((u) => (
            <div key={u.id} className="cu-row">
              <div>
                <strong>{u.full_name || u.email}</strong> {pill(u.status)}
                <div className="cell-sub">{u.email} · invited {fmtD(u.invited_at)}
                  {u.last_seen_at && <> · last seen {fmtD(u.last_seen_at)}</>}</div>
              </div>
              {u.status !== 'disabled' ? (
                <button className="link-btn" onClick={() =>
                  setPortalUserStatus(u.id, 'disabled').then(reload).catch((e) => onMsg(e.message))}>Disable</button>
              ) : (
                <button className="link-btn" onClick={() =>
                  setPortalUserStatus(u.id, 'active').then(reload).catch((e) => onMsg(e.message))}>Re-enable</button>
              )}
            </div>
          ))}
        </section>

        <section className="cu-section">
          <div className="cu-sec-head"><h3>History</h3></div>
          {audit.length === 0 && <p className="muted">No material changes recorded yet.</p>}
          {audit.map((h) => (
            <div key={h.id} className="cu-audit">
              <span className="cu-audit-when">{new Date(h.created_at).toLocaleString('en-GB',
                { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              <span className={`pill ${h.source === 'atlas' ? 'info' : h.source === 'operator' ? 'ok' : 'mute'}`}>{h.source}</span>
              <span className="cu-audit-body">
                {h.field === 'created' ? h.new_value
                  : h.field === 'contact' ? `Contact: ${h.new_value ?? h.old_value}`
                  : h.field === 'distributor' ? `Distributor: ${h.old_value ?? 'none'} → ${h.new_value ?? 'none'}`
                  : `${h.field.replace(/_/g, ' ')}: ${h.old_value ?? '(empty)'} → ${h.new_value ?? '(empty)'}`}
              </span>
            </div>
          ))}
        </section>

        {addingIntel && (
          <AddIntel accountId={a.id} linked={!!a.atlas_site_id}
            onClose={() => setAddingIntel(false)}
            onDone={() => { setAddingIntel(false); onMsg('Intel recorded.'); reload(); }} />
        )}
        {inviting && (
          <InviteUser accountId={a.id}
            onClose={() => setInviting(false)}
            onDone={(email) => { setInviting(false); onMsg(`Invitation sent to ${email}.`); reload(); }}
            onErr={(m) => { setInviting(false); setErr(m); }} />
        )}
      </div>
    </>
  );
}

function AddIntel({ accountId, linked, onClose, onDone }: {
  accountId: string; linked: boolean; onClose: () => void; onDone: () => void;
}) {
  const [metric, setMetric] = useState('deployed_platform');
  const [text, setText] = useState('');
  const [organ, setOrgan] = useState('kidney');
  const [year, setYear] = useState(new Date().getFullYear());
  const [total, setTotal] = useState('');
  const [source, setSource] = useState('conversation');
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const isVolume = metric === 'transplant_volume';
  return (
    <div className="cu-subform">
      <h3>Record intel</h3>
      {err && <p className="block-note">{err}</p>}
      <label className="cf-field"><span>What was learned</span>
        <select className="input sel" value={metric} onChange={(e) => setMetric(e.target.value)}>
          {INTEL_METRICS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select></label>
      {isVolume ? (
        <div className="cu-two">
          <label className="cf-field"><span>Activity</span>
            <input className="input" value={organ} onChange={(e) => setOrgan(e.target.value)} /></label>
          <label className="cf-field"><span>Year · annual total</span>
            <span className="cu-pair">
              <input className="input" type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} />
              <input className="input" type="number" value={total} placeholder="total" onChange={(e) => setTotal(e.target.value)} />
            </span></label>
        </div>
      ) : (
        <label className="cf-field"><span>Value</span>
          <input className="input" value={text} onChange={(e) => setText(e.target.value)} /></label>
      )}
      <div className="cu-two">
        <label className="cf-field"><span>Source</span>
          <select className="input sel" value={source} onChange={(e) => setSource(e.target.value)}>
            {INTEL_SOURCES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select></label>
        <label className="cf-field"><span>Note</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></label>
      </div>
      {isVolume && !linked && (
        <p className="muted">This account has no Atlas site link yet, so the volume can be recorded but not applied.</p>
      )}
      <div className="exp-decide-actions">
        <button className="btn primary" onClick={async () => {
          setErr(null);
          try {
            const value = isVolume
              ? { organ, data_year: year, total: Number(total) }
              : { text };
            if (isVolume && !total) throw new Error('The annual total is the point.');
            if (!isVolume && !text.trim()) throw new Error('Say what was learned.');
            await addIntel({ accountId, metric, value, source, note: note || undefined });
            onDone();
          } catch (e) { setErr(String((e as Error).message)); }
        }}>Record</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

function InviteUser({ accountId, onClose, onDone, onErr }: {
  accountId: string; onClose: () => void; onDone: (email: string) => void; onErr: (m: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="cu-subform">
      <h3>Invite a contact to the portal</h3>
      <p className="muted">They get an email with a sign-in link; they see only their own account's quotes, orders and shipments.</p>
      <div className="cu-two">
        <label className="cf-field"><span>Email</span>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <label className="cf-field"><span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} /></label>
      </div>
      <div className="exp-decide-actions">
        <button className="btn primary" disabled={busy} onClick={async () => {
          setBusy(true);
          try {
            if (!/.+@.+\..+/.test(email)) throw new Error('A real email address is needed.');
            await invitePortalUser(accountId, email.trim(), name.trim() || undefined);
            onDone(email.trim());
          } catch (e) { onErr(String((e as Error).message)); }
        }}>{busy ? 'Inviting…' : 'Send invitation'}</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}


function AddContactForm({ accountId, onClose, onDone }: {
  accountId: string; onClose: () => void; onDone: () => void;
}) {
  const [f, setF] = useState({ firstName: '', lastName: '', roleTitle: '', email: '', phone: '', notes: '' });
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="cu-subform">
      <h3>Add a contact</h3>
      {err && <p className="block-note">{err}</p>}
      <div className="cu-two">
        <label className="cf-field"><span>First name</span>
          <input className="input" value={f.firstName} onChange={set('firstName')} /></label>
        <label className="cf-field"><span>Last name</span>
          <input className="input" value={f.lastName} onChange={set('lastName')} /></label>
      </div>
      <div className="cu-two">
        <label className="cf-field"><span>Role (lab director, surgeon, procurement…)</span>
          <input className="input" value={f.roleTitle} onChange={set('roleTitle')} /></label>
        <label className="cf-field"><span>Email</span>
          <input className="input" type="email" value={f.email} onChange={set('email')} /></label>
      </div>
      <div className="cu-two">
        <label className="cf-field"><span>Phone</span>
          <input className="input" value={f.phone} onChange={set('phone')} /></label>
        <label className="cf-field"><span>Comment</span>
          <input className="input" value={f.notes} onChange={set('notes')} /></label>
      </div>
      <div className="exp-decide-actions">
        <button className="btn primary" onClick={async () => {
          try {
            if (!f.lastName.trim()) throw new Error('At least a last name.');
            await addContact({ accountId, firstName: f.firstName || undefined,
              lastName: f.lastName.trim(), roleTitle: f.roleTitle || undefined,
              email: f.email || undefined, phone: f.phone || undefined,
              notes: f.notes || undefined });
            onDone();
          } catch (e) { setErr(String((e as Error).message)); }
        }}>Add</button>
        <button className="btn" onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}